import { useState, useEffect } from 'react'
import type { ColumnSizingState } from '@tanstack/react-table'

/**
 * Custom hook for persisting column widths to localStorage
 *
 * @param storageKey - Unique key for localStorage (e.g., 'tasks-column-widths')
 * @param defaultWidths - Default column widths object
 * @returns [columnSizing, setColumnSizing] - State and setter for column sizing
 */
export function useColumnWidths(
  storageKey: string,
  defaultWidths: ColumnSizingState = {}
): [ColumnSizingState, React.Dispatch<React.SetStateAction<ColumnSizingState>>] {
  // Initialize state from localStorage or defaults
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Merge stored widths with defaults to handle new columns
        return { ...defaultWidths, ...parsed }
      }
    } catch (error) {
      console.error('Error loading column widths from localStorage:', error)
    }
    return defaultWidths
  })

  // Save to localStorage whenever column sizing changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnSizing))
    } catch (error) {
      console.error('Error saving column widths to localStorage:', error)
    }
  }, [columnSizing, storageKey])

  return [columnSizing, setColumnSizing]
}
